import { useEffect } from 'react';
import { useAppDispatch } from './store/hooks';
import { BrowserRouter as Router } from 'react-router-dom';
import { APIProvider } from '@vis.gl/react-google-maps';

import Routes from '@app/routes';
import ScrollToTop from '@app/components/utils/scroll-to-top';

import CartService from './services/cart.service';
import { setStoredCartProducts, setStoredCartSyncronized, setCartLoadFailed, setVendorExpiry, setExpiredNotices } from './store/cart.store';
import { setSettings } from './store/settings.store';
import { apiGet, parseApiError } from '@app/utils/http';

import { GTMProvider } from '@app/utils/gtm-provider';

import '@app/design-system/tokens.css';
import '@app/scss/theme/theme.scss';

function App() {
    const dispatch = useAppDispatch();
    const gtmId = process.env.GTAG_ID || '';

    useEffect(() => {
        apiGet('@api/settings', true)
            .then((settings: any) => {
                window.const = settings;
                dispatch(setSettings({
                    loginEnabled: settings.loginEnabled === 'true',
                    promoEnabled: settings.promoEnabled === 'true',
                    promos: Array.isArray(settings.promos) ? settings.promos : [],
                    elastic: settings.elastic === 'true',
                }));
            })
            .catch(async (err) => {
                const parsed = await parseApiError(err);
                console.error('[SETTINGS] Initial load failed:', parsed);
                dispatch(setSettings({
                    loginEnabled: false,
                    promoEnabled: false,
                    promos: [],
                    elastic: true,
                }));
            });
    }, []);

    useEffect(() => {
        CartService.get()
            .then(response => {
                const products: { [key: number]: number } = {};

                response.items.forEach(item => {
                    products[item.wcId] = products[item.wcId]
                            ? products[item.wcId] + item.quantity
                            : item.quantity;
                });

                dispatch(setStoredCartProducts(products));
                dispatch(setStoredCartSyncronized(true));
                dispatch(setCartLoadFailed(false));

                // Extract vendor expiry data for countdown badge
                const vendorExpiry: Record<number, { name: string; defaultHide: string }> = {};
                response.vendors?.forEach((v: any) => {
                    if (v.defaultHide) {
                        vendorExpiry[v.id] = { name: v.name, defaultHide: v.defaultHide };
                    }
                });
                dispatch(setVendorExpiry(vendorExpiry));

                // Surface expired product notices
                if (response.expiredItems?.length) {
                    console.error('[CART] Items expired:', response.expiredItems);
                    dispatch(setExpiredNotices(response.expiredItems));
                }
            })
            .catch(async (err) => {
                const parsed = await parseApiError(err);
                console.error('[CART] Initial load failed:', parsed);

                dispatch(setStoredCartProducts({}));
                dispatch(setStoredCartSyncronized(true));
                dispatch(setCartLoadFailed(true));
            });
    }, []);

    return (
        <APIProvider
            apiKey={process.env.GMAPS_KEY}
            libraries={['places']}
            language="en">
            <GTMProvider id={gtmId}>
                <Router>
                    <ScrollToTop>
                        <div id={`page`}>
                            <Routes />
                        </div>
                    </ScrollToTop>
                </Router>
            </GTMProvider>
        </APIProvider>
    );
}

export default App;